// Remove uuidV4 import - using crypto.randomUUID() instead

const escapeStack = [];
let initialized = false;

const initialize = () => {
  if (initialized) return;
  document.body.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && escapeStack.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      escapeStack.at(-1).callback(e);
    }
  });
  initialized = true;
};

/**
 * The return value of this function should be bound to a piece of reactive
 * state:
 *
 *     $: windowClick = makeWindowClick(close, referenceEl, floatingEl);
 *
 * So that the window click handler is regenerated when referenceEl/floatingEl
 * changes.
 */
export const makeWindowClick = (callback, referenceEl, floatingEl) => (e) => {
  // Check composedPath for Shadow DOM
  const target = e.composedPath()[0] || e.target;
  if (!referenceEl || !floatingEl) return;
  if (floatingEl.contains(target) || referenceEl.contains(target)) return;
  callback();
};

/**
 * Pushes the callback function onto a stack. When the escape key is pressed,
 * the top of the callback on top is executed. This architecture allows stacks
 * of popups to appear, each can be dismissed via the escape function.
 *
 * @param {() => void} callback - When called should remove component from DOM.
 */
export const makeEscapeAction = (callback) => () => {
  initialize();

  const id = crypto.randomUUID();
  escapeStack.push({ id, callback });
  return {
    destroy: () => {
      const index = escapeStack.findIndex((x) => x.id === id);
      if (index !== -1) escapeStack.splice(index, 1);
    },
  };
};

export const makeOnMouseDownUpAction = (callback) => {
  let mouseDownOn = false;

  return (el) => {
    // Check whether the event happened on the node (not children)
    const happenedOn = (event) =>
      event.target === el && !event.defaultPrevented;

    // Set mouseDownOn to true if the mouse is pressed on the node
    const handleMouseDown = (event) => {
      mouseDownOn = happenedOn(event);
    };

    // If mouse was pressed outside, AND is released outside, call the callback
    const handleMouseUp = (event) => {
      if (mouseDownOn && happenedOn(event)) callback();
      mouseDownOn = false;
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("mouseup", handleMouseUp, true);

    return {
      destroy: () => {
        document.removeEventListener("mousedown", handleMouseDown, true);
        document.removeEventListener("mouseup", handleMouseUp, true);
      },
    };
  };
};
